"use client";

import { useEffect, useRef, useMemo } from "react";
import Image from "next/image";

interface Book {
  id: string;
  alt: string;
}

function fibSpherePoints(n: number): [number, number, number][] {
  const gr = (1 + Math.sqrt(5)) / 2;
  return Array.from({ length: n }, (_, i) => {
    const theta = Math.acos(1 - (2 * (i + 0.5)) / n);
    const phi = (2 * Math.PI * i) / gr;
    return [
      Math.sin(theta) * Math.cos(phi),
      Math.sin(theta) * Math.sin(phi),
      Math.cos(theta),
    ] as [number, number, number];
  });
}

const RADIUS = 400;
const CARD_W = 100;
const CARD_H = 150;

export default function SphereCarousel({ books }: { books: Book[] }) {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const rafRef = useRef<number>(0);

  const basePoints = useMemo(() => fibSpherePoints(books.length), [books.length]);
  const bpRef = useRef(basePoints);
  bpRef.current = basePoints;

  const stateRef = useRef({
    rotY: 0,
    rotX: 0.28,
    drag: { active: false, lastX: 0, velX: 0 },
  });

  useEffect(() => {
    const s = stateRef.current;
    const cards = cardRefs.current;

    const tick = () => {
      if (!s.drag.active) {
        s.rotY += 0.0028;
        s.drag.velX *= 0.94;
        s.rotY += s.drag.velX;
      }

      const cosY = Math.cos(s.rotY);
      const sinY = Math.sin(s.rotY);
      const cosX = Math.cos(s.rotX);
      const sinX = Math.sin(s.rotX);

      bpRef.current.forEach(([bx, by, bz], i) => {
        const card = cards[i];
        if (!card) return;

        const x1 = bx * cosY + bz * sinY;
        const z1 = -bx * sinY + bz * cosY;
        const y2 = by * cosX - z1 * sinX;
        const z2 = by * sinX + z1 * cosX;

        const wx = x1 * RADIUS;
        const wy = y2 * RADIUS;
        const wz = z2 * RADIUS;

        const depth = (z2 + 1) / 2;
        const opacity = (0.1 + depth * 0.9).toFixed(3);
        const zIdx = Math.round(depth * 100);

        card.style.transform = `translate(-50%,-50%) translate3d(${wx}px,${wy}px,${wz}px)`;
        card.style.opacity = opacity;
        card.style.zIndex = String(zIdx);
      });

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    stateRef.current.drag = { active: true, lastX: e.clientX, velX: 0 };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const s = stateRef.current;
    if (!s.drag.active) return;
    const dx = e.clientX - s.drag.lastX;
    s.drag.velX = dx * 0.004;
    s.rotY += dx * 0.006;
    s.drag.lastX = e.clientX;
  };
  const onPointerUp = () => {
    stateRef.current.drag.active = false;
  };

  return (
    <div
      className="relative w-full select-none cursor-grab active:cursor-grabbing"
      style={{ height: 1100 }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-[500px] w-[500px] rounded-full bg-green-300/15 blur-[120px]" />
      </div>

      {/* 3D scene */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ perspective: "1400px" }}
      >
        <div style={{ width: 0, height: 0, transformStyle: "preserve-3d", position: "relative" }}>
          {books.map((book, i) => (
            <div
              key={book.id}
              ref={(el) => { cardRefs.current[i] = el; }}
              className="absolute overflow-hidden rounded-[6px] border border-white/20 shadow-2xl"
              style={{
                width: CARD_W,
                height: CARD_H,
                top: 0,
                left: 0,
                willChange: "transform, opacity",
              }}
            >
              <Image
                src={`https://s3-us-west-2.amazonaws.com/s.cdpn.io/881020/${book.id}.jpg`}
                alt={book.alt}
                fill
                unoptimized
                draggable={false}
                className="object-cover"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Drag hint */}
      <p className="pointer-events-none absolute bottom-6 left-0 right-0 text-center text-[11px] font-medium uppercase tracking-[0.2em] text-gray-400">
        Drag to spin
      </p>
    </div>
  );
}
